import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Heart, ShoppingBag, Video, Gift as GiftIcon } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TITHES = [
  { value: 0, label: "None" },
  { value: 1, label: "$1" },
  { value: 3, label: "$3" },
  { value: 5, label: "$5" },
];

export default function BuyDialog({ open, onOpenChange, song, format }) {
  const [email, setEmail] = useState("");
  const [tithe, setTithe] = useState(0);
  const [isGift, setIsGift] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [giftMessage, setGiftMessage] = useState("");
  const [loading, setLoading] = useState(false);

  if (!song) return null;
  const price = format === "mp4" ? song.price_mp4 : song.price_mp3;
  const total = price + tithe;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Please enter a valid email"); return; }
    if (isGift && !recipientEmail.includes("@")) { toast.error("Enter a recipient email for the gift"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        song_id: song.id, format, email, tithe_amount: tithe,
        is_gift: isGift,
        recipient_email: isGift ? recipientEmail : undefined,
        gift_message: isGift ? giftMessage : undefined,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl border-[--sanctum-border]" data-testid="buy-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-normal flex items-center gap-2">
            {isGift ? <GiftIcon size={18} strokeWidth={1.5} /> :
              format === "mp4" ? <Video size={18} strokeWidth={1.5} /> : <ShoppingBag size={18} strokeWidth={1.5} />}
            {isGift ? "Send as a gift" : (format === "mp4" ? "Buy MP4 video" : "Buy MP3 track")}
          </DialogTitle>
          <DialogDescription className="text-sm text-neutral-500">
            <span className="text-neutral-800 font-medium">{song.title}</span> · {song.artist} · ${price.toFixed(2)}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2">
          <div>
            <Label htmlFor="buy-email" className="text-xs tracking-widest uppercase text-neutral-500 mb-1.5 block">Your email</Label>
            <Input
              id="buy-email" data-testid="buy-email-input"
              type="email" required autoFocus
              placeholder="you@example.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="h-11 rounded-xl"
            />
          </div>

          {/* Gift toggle */}
          <button
            type="button"
            data-testid="gift-toggle"
            onClick={() => setIsGift((v) => !v)}
            className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${
              isGift ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-[--sanctum-border] hover:border-neutral-400"
            }`}
          >
            <GiftIcon size={16} strokeWidth={1.5} />
            <div className="flex-1">
              <div className="text-sm font-medium">Send as a gift</div>
              <div className={`text-[11px] ${isGift ? "text-white/60" : "text-neutral-500"}`}>
                We'll email the song to a friend instead of you.
              </div>
            </div>
            <div className={`w-9 h-5 rounded-full flex items-center px-0.5 ${isGift ? "bg-white/25 justify-end" : "bg-neutral-200 justify-start"}`}>
              <span className={`w-4 h-4 rounded-full ${isGift ? "bg-white" : "bg-white"}`} />
            </div>
          </button>

          {isGift && (
            <div className="space-y-3 pl-1 fade-up">
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-500 mb-1.5 block">Recipient email</Label>
                <Input
                  type="email" required data-testid="gift-recipient-email"
                  placeholder="friend@example.com"
                  value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)}
                  className="h-11 rounded-xl"
                />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-500 mb-1.5 block">A short note (optional)</Label>
                <Textarea
                  rows={2}
                  data-testid="gift-message"
                  placeholder="Thinking of you — this one made me pause."
                  value={giftMessage} onChange={(e) => setGiftMessage(e.target.value)}
                  className="rounded-xl resize-none"
                />
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center gap-2 mb-2">
              <Heart size={13} strokeWidth={1.5} className="text-[--sanctum-accent]" />
              <Label className="text-xs tracking-widest uppercase text-neutral-500">Add an offering</Label>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {TITHES.map((t) => (
                <button
                  key={t.value} type="button"
                  data-testid={`tithe-${t.value}`}
                  onClick={() => setTithe(t.value)}
                  className={`h-10 rounded-full text-sm font-medium transition-colors border ${
                    tithe === t.value
                      ? "bg-neutral-900 text-white border-neutral-900"
                      : "bg-white text-neutral-700 border-[--sanctum-border] hover:border-neutral-400"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl bg-[--sanctum-secondary]/40 p-4 text-sm space-y-1.5">
            <div className="flex justify-between text-neutral-600"><span>{format === "mp4" ? "MP4 video" : "MP3 track"}</span><span>${price.toFixed(2)}</span></div>
            {tithe > 0 && <div className="flex justify-between text-neutral-600"><span>Offering</span><span>${tithe.toFixed(2)}</span></div>}
            <div className="flex justify-between font-semibold pt-1.5 border-t border-[--sanctum-border]">
              <span>Total</span><span data-testid="buy-total">${total.toFixed(2)}</span>
            </div>
          </div>

          <DialogFooter className="!flex-col-reverse sm:!flex-row sm:!justify-between gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="buy-cancel">Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="buy-submit" className="rounded-full bg-neutral-900 hover:bg-black">
              {loading ? "Redirecting…" : `Continue · $${total.toFixed(2)}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
